/**
 * Spreader model + product catalog and the VERIFIED dial-setting table behind
 * /tools/spreader-settings. Pure data, no side effects.
 *
 * SOURCING RULE (enforceable, not aspirational):
 * A row may be added to SPREADER_SETTING_ROWS ONLY from a product label
 * (photo/PDF) or a manufacturer's published settings chart, with the citation
 * string and the verification month recorded on the row. If you cannot cite it,
 * leave it out and let the calibration fallback answer. A guard test in
 * __tests__/lib/spreader-settings.test.ts fails the build if a row is missing
 * a citation or verifiedOn, or if a row is keyed to the 'unknown' scale.
 *
 * There is no "estimated" setting anywhere in this file or in the engine's
 * return type. A guessed dial number costs a bag of product or a burned lawn,
 * so the tool answers with the target rate per 1,000 sq ft plus a calibration
 * method instead.
 *
 * Rates are DERIVED, never retyped: granular/liquid rates come from
 * data/fertilizer-products.ts (`labelRatePer1000`, which carries its own
 * `labelSource`) or from data/weed-and-feed-products.ts bag arithmetic
 * (weightLbs / coverageSqFt * 1000).
 */

import { FERTILIZER_PRODUCTS } from '@/data/fertilizer-products'
import { WEED_AND_FEED_PRODUCTS } from '@/data/weed-and-feed-products'
import type { ProductKey } from '@/data/guide-product-recommendations'

// ---------------------------------------------------------------------------
// Spreader models
// ---------------------------------------------------------------------------

export type SpreaderType = 'broadcast' | 'drop' | 'handheld'

/**
 * Which published setting SCALE a model's dial reads on. Rows are keyed to a
 * scale, not a model, so one verified label row serves every model that shares
 * the scale. NEVER assign a scale you cannot source: 'unknown' is the honest
 * value for a brand whose dial has no published cross-reference in this repo.
 */
export type SettingScaleId =
  | 'scotts-rotary'
  | 'scotts-drop'
  | 'generic-rotary'
  | 'generic-drop'
  | 'unknown'

export interface SpreaderModel {
  id: string
  brand: string
  name: string
  type: SpreaderType
  scaleId: SettingScaleId
  /** Optional affiliate join; omit when we track no SKU. */
  productKey?: ProductKey
  /** Search aliases for the on-page model list. */
  aliases?: string[]
  /** Provenance for anything asserted about this model. */
  source?: string
}

export const SPREADER_MODELS: SpreaderModel[] = [
  {
    id: 'scotts-edgeguard-mini',
    brand: 'Scotts',
    name: 'Scotts Turf Builder EdgeGuard Mini',
    type: 'broadcast',
    scaleId: 'scotts-rotary',
    productKey: 'tool-spreader-edgeguard-mini',
    aliases: ['EdgeGuard Mini', 'Scotts Mini broadcast spreader'],
    source: 'Scotts prints a rotary/broadcast setting on its own bag labels.',
  },
  {
    id: 'scotts-edgeguard-dlx',
    brand: 'Scotts',
    name: 'Scotts Turf Builder EdgeGuard DLX',
    type: 'broadcast',
    scaleId: 'scotts-rotary',
    aliases: ['EdgeGuard DLX', 'Scotts DLX'],
    source: 'Scotts prints a rotary/broadcast setting on its own bag labels.',
  },
  {
    id: 'scotts-elite',
    brand: 'Scotts',
    name: 'Scotts Elite Spreader',
    type: 'broadcast',
    scaleId: 'scotts-rotary',
    aliases: ['Scotts Elite', 'Elite dual rotary'],
    source: 'Scotts prints a rotary/broadcast setting on its own bag labels.',
  },
  {
    id: 'scotts-wizz',
    brand: 'Scotts',
    name: 'Scotts Wizz Handheld Spreader',
    type: 'handheld',
    // The Wizz meters on its OWN dial. Scotts bags print a rotary number and a
    // drop number, neither of which is a Wizz number, and no Wizz cross-
    // reference is sourced in this repo. Assigning 'scotts-rotary' here would
    // hand out a push-spreader number under a "from the bag label" line.
    scaleId: 'unknown',
    aliases: ['Scotts Wizz', 'handheld spreader'],
    source: 'The Wizz has its own dial, separate from the rotary and drop numbers Scotts prints on bags.',
  },
  {
    id: 'scotts-classic-drop',
    brand: 'Scotts',
    name: 'Scotts Turf Builder Classic Drop Spreader',
    type: 'drop',
    scaleId: 'scotts-drop',
    aliases: ['Scotts Classic drop', 'Scotts drop spreader'],
    // Deliberately unlinked: the affiliate catalog refuses to map this anchor
    // because the SKU is discontinued and the listings that remain are resellers.
    source: 'Scotts prints a separate drop-spreader setting on its own bag labels.',
  },
  {
    id: 'agri-fab-broadcast',
    brand: 'Agri-Fab',
    name: 'Agri-Fab broadcast spreader',
    type: 'broadcast',
    scaleId: 'unknown',
    aliases: ['Agri-Fab tow-behind', 'Agri-Fab push spreader'],
  },
  {
    id: 'chapin-broadcast',
    brand: 'Chapin',
    name: 'Chapin broadcast spreader',
    type: 'broadcast',
    scaleId: 'unknown',
    aliases: ['Chapin push spreader', 'Chapin 8620B'],
  },
  {
    id: 'earthway-broadcast',
    brand: 'EarthWay',
    name: 'EarthWay broadcast spreader',
    type: 'broadcast',
    scaleId: 'unknown',
    aliases: ['EarthWay 2150', 'EarthWay Ev-N-Spred'],
  },
  {
    id: 'lesco-rotary',
    brand: 'Lesco',
    name: 'Lesco rotary spreader',
    type: 'broadcast',
    scaleId: 'unknown',
    aliases: ['Lesco 80 lb spreader', 'Lesco pro spreader'],
  },
  {
    id: 'generic-broadcast',
    brand: 'Any brand',
    name: 'Other broadcast or rotary spreader',
    type: 'broadcast',
    scaleId: 'generic-rotary',
  },
  {
    id: 'generic-drop',
    brand: 'Any brand',
    name: 'Other drop spreader',
    type: 'drop',
    scaleId: 'generic-drop',
  },
]

export const SPREADER_TYPE_LABEL: Record<SpreaderType, string> = {
  broadcast: 'Broadcast',
  drop: 'Drop',
  handheld: 'Handheld',
}

export function getSpreaderModel(id: string): SpreaderModel | undefined {
  return SPREADER_MODELS.find((m) => m.id === id)
}

// ---------------------------------------------------------------------------
// Products (derived from the two existing label catalogs)
// ---------------------------------------------------------------------------

export interface SpreaderProduct {
  id: string
  name: string
  short: string
  form: 'granular' | 'liquid'
  npk?: string
  /** lb per 1,000 sq ft (granular) or fl oz per 1,000 sq ft (liquid). */
  ratePer1000: number
  rateUnit: 'lb' | 'fl oz'
  /** Preformatted, e.g. "12.8 lbs". */
  rateLabel: string
  /** Preformatted, e.g. "32 lb / 2,500 sq ft". */
  coverageLabel: string
  /** Carried through verbatim for the methodology line. */
  labelSource: string
  sourceCatalog: 'fertilizer-products' | 'weed-and-feed-products'
  /** This product's own calculator route, when it has one. */
  calculatorSlug?: string
}

/**
 * Weed-and-feed catalog entries that have no counterpart in
 * data/fertilizer-products.ts. Identity and provenance are authored here; the
 * NUMBERS are still derived from the bag arithmetic below.
 *
 * Scotts Bonus S (29-0-10) is a different SKU from Scotts Southern Lawn Food
 * (32-0-10), which is why it keeps its own id rather than merging into
 * 'southern'.
 */
const WEED_FEED_ONLY: Record<string, { id: string; short: string; labelSource: string }> = {
  bonus: {
    id: 'southern-bonus-s',
    short: 'Bonus S · 29-0-10',
    labelSource:
      'Rate derived from the Scotts Turf Builder Bonus S bag coverage claim (17.24 lb per 5,000 sq ft), verified Jun 2026.',
  },
  fert: {
    id: 'fertilome-centipede',
    short: 'Fertilome 15-0-15',
    labelSource:
      'Rate derived from the Fertilome Centipede Weed & Feed bag coverage claim (16 lb per 5,000 sq ft), verified Jun 2026.',
  },
  soag: {
    id: 'southern-ag-atrazine',
    short: 'Southern Ag Atrazine · liquid',
    labelSource:
      'Rate from the Southern Ag Atrazine label (one 32 oz quart treats about 3,720 sq ft), verified Jun 2026.',
  },
}

function roundTo(value: number, places: number): number {
  const f = 10 ** places
  return Math.round(value * f) / f
}

function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : String(roundTo(value, 1))
}

const FERTILIZER_ENTRIES: SpreaderProduct[] = FERTILIZER_PRODUCTS.map((p) => ({
  id: p.id,
  name: p.name,
  short: p.short,
  form: p.form,
  npk: p.npk,
  ratePer1000: p.labelRatePer1000,
  rateUnit: p.form === 'liquid' ? ('fl oz' as const) : ('lb' as const),
  rateLabel: p.rateLabel,
  coverageLabel: p.bagLabel,
  labelSource: p.labelSource,
  sourceCatalog: 'fertilizer-products' as const,
  calculatorSlug: p.slug,
}))

const WEED_FEED_ENTRIES: SpreaderProduct[] = WEED_AND_FEED_PRODUCTS.flatMap((p): SpreaderProduct[] => {
  const meta = WEED_FEED_ONLY[p.id]
  // wf3 and ta already exist in FERTILIZER_PRODUCTS (as 'wf3' and 'triple');
  // the fertilizer-products row is canonical because it carries labelSource.
  if (!meta) return []

  if (p.form === 'liquid' && p.liquidRate) {
    return [
      {
        id: meta.id,
        name: p.name,
        short: meta.short,
        form: 'liquid' as const,
        ratePer1000: p.liquidRate.ozPer1000SqFt,
        rateUnit: 'fl oz' as const,
        rateLabel: `${formatNumber(p.liquidRate.ozPer1000SqFt)} fl oz`,
        coverageLabel: `${p.liquidRate.containerOz} oz / ${p.liquidRate.coveragePerContainerSqFt.toLocaleString('en-US')} sq ft`,
        labelSource: meta.labelSource,
        sourceCatalog: 'weed-and-feed-products' as const,
      },
    ]
  }

  const bag = p.bagOptions?.[0]
  if (!bag) return []
  const rate = roundTo((bag.weightLbs / bag.coverageSqFt) * 1000, 2)
  return [
    {
      id: meta.id,
      name: p.name,
      short: meta.short,
      form: 'granular' as const,
      npk: p.npk.join('-'),
      ratePer1000: rate,
      rateUnit: 'lb' as const,
      rateLabel: `${formatNumber(rate)} lbs`,
      coverageLabel: `${formatNumber(bag.weightLbs)} lb / ${bag.coverageSqFt.toLocaleString('en-US')} sq ft`,
      labelSource: meta.labelSource,
      sourceCatalog: 'weed-and-feed-products' as const,
    },
  ]
})

export const SPREADER_PRODUCTS: SpreaderProduct[] = [...FERTILIZER_ENTRIES, ...WEED_FEED_ENTRIES]

/**
 * Ids the two source catalogs use for the SAME physical product, plus the
 * fertilizer-product slugs, so a deep link from any sibling tool resolves.
 */
export const SPREADER_PRODUCT_ALIASES: Record<string, string> = {
  ta: 'triple',
  bonus: 'southern-bonus-s',
  fert: 'fertilome-centipede',
  soag: 'southern-ag-atrazine',
  ...Object.fromEntries(FERTILIZER_PRODUCTS.map((p) => [p.slug, p.id])),
}

export function getSpreaderProduct(id: string): SpreaderProduct | undefined {
  const resolved = SPREADER_PRODUCT_ALIASES[id] ?? id
  return SPREADER_PRODUCTS.find((p) => p.id === resolved)
}

// ---------------------------------------------------------------------------
// Verified settings
// ---------------------------------------------------------------------------

export type SettingSource = 'product-label' | 'manufacturer-chart'

export interface SpreaderSettingRow {
  productId: string
  scaleId: SettingScaleId
  /** The printed dial value exactly as the label prints it, as a STRING. */
  setting: string
  source: SettingSource
  citation: string
  /** YYYY-MM. */
  verifiedOn: string
  note?: string
}

/**
 * Two different bags ship under the Bonus S name and they are not close on the
 * dial. Verified 2026-08 on Scotts' own product pages: SKU 3313B (Southern
 * Weed & Feed₂) calls for 4¼ rotary and 7¼ drop, SKU 21030A (the Florida
 * bag) calls for 5¼ rotary and 11 drop. Both are 17.24 lb over 5,000 sq ft, so
 * bag size does not tell them apart.
 */
export const BONUS_S_SKU_NOTE =
  'Scotts sells two Bonus S bags under nearly the same name. The Florida bag (SKU 21030A) calls for 5¼ rotary and 11 drop instead, which is about half again as much gate on the drop scale, so match the number to the exact bag you bought.'

/**
 * The complete set of dial numbers this site is willing to publish. Four rows.
 * Both products are Scotts, because Scotts is the only manufacturer in these
 * catalogs that prints a dial number on the bag. Values and provenance copied
 * from the per-product comments in data/weed-and-feed-products.ts.
 */
export const SPREADER_SETTING_ROWS: SpreaderSettingRow[] = [
  {
    productId: 'wf3',
    scaleId: 'scotts-rotary',
    setting: '3½',
    source: 'product-label',
    citation: 'Scotts Turf Builder Weed & Feed 3 bag label',
    verifiedOn: '2026-06',
  },
  {
    productId: 'wf3',
    scaleId: 'scotts-drop',
    setting: '5',
    source: 'product-label',
    citation: 'Scotts Turf Builder Weed & Feed 3 bag label',
    verifiedOn: '2026-06',
  },
  {
    productId: 'southern-bonus-s',
    scaleId: 'scotts-rotary',
    setting: '4¼',
    source: 'product-label',
    citation: 'Scotts Turf Builder Bonus S Southern Weed & Feed₂ directions (SKU 3313B)',
    verifiedOn: '2026-08',
    note: BONUS_S_SKU_NOTE,
  },
  {
    productId: 'southern-bonus-s',
    scaleId: 'scotts-drop',
    setting: '7¼',
    source: 'product-label',
    citation: 'Scotts Turf Builder Bonus S Southern Weed & Feed₂ directions (SKU 3313B)',
    verifiedOn: '2026-08',
    note: BONUS_S_SKU_NOTE,
  },
]

/**
 * Products whose label DOES print a dial number, but where the number changes
 * by bag size or variant. Publishing one would be right for some buyers and
 * wrong for the rest, so the tool sends people to the bag instead.
 */
export const LABEL_DEFER_PRODUCT_IDS: string[] = ['triple', 'fertilome-centipede']

export const LABEL_DEFER_REASON =
  'Scotts prints a dial number for this one, but it changes by bag size and variant, so the only number worth trusting is the one on the bag in your garage.'

/**
 * Products whose own directions forbid a whole class of applicator. This is a
 * label claim, not our opinion, so every entry carries the citation and the
 * month it was read, exactly like a setting row.
 *
 * The engine surfaces this INSTEAD of a rate or a dial number. Handing someone
 * a rate for equipment the label prohibits is the same failure as inventing a
 * dial number, one step further along: it reads as permission.
 */
export interface HandheldProhibition {
  productId: string
  /** Standalone sentences. Rendered as the whole answer, not appended to one. */
  copy: string
  citation: string
  /** YYYY-MM. */
  verifiedOn: string
}

export const HANDHELD_PROHIBITIONS: HandheldProhibition[] = [
  {
    productId: 'southern-bonus-s',
    copy:
      'The label prohibits hand-held application for this product. The Florida Bonus S bag prints "Do not apply with a hand-held spreader" in its directions, and it sits next to the other Bonus S bag under nearly the same name at the same weight and coverage. Since we cannot tell which one you carried home, this tool gives no hand-held number for either. Put it through a broadcast or a drop spreader, and read your own bag before you spread it.',
    citation:
      'Scotts Turf Builder Bonus S Southern Weed & FeedF₂ Florida Fertilizer directions (SKU 21030A)',
    verifiedOn: '2026-08',
  },
]

export function findHandheldProhibition(productId: string): HandheldProhibition | undefined {
  const resolved = SPREADER_PRODUCT_ALIASES[productId] ?? productId
  return HANDHELD_PROHIBITIONS.find((p) => p.productId === resolved)
}

/** The standing line that rides on every granular result. */
export const CONFIRM_AGAINST_BAG =
  'Always confirm against your bag label. The label is the law.'

/** Same line for liquids, which come in a jug rather than a bag. */
export const CONFIRM_AGAINST_LABEL =
  'Always confirm against the product label. The label is the law.'

export function findSettingRow(productId: string, scaleId: SettingScaleId): SpreaderSettingRow | undefined {
  const resolved = SPREADER_PRODUCT_ALIASES[productId] ?? productId
  return SPREADER_SETTING_ROWS.find((r) => r.productId === resolved && r.scaleId === scaleId)
}
